import { useState, useMemo } from 'react';
import ProjectContextBadge from '../components/layout/ProjectContextBadge';
import Modal from '../components/ui/Modal';
import Badge from '../components/ui/Badge';
import useProjectStore from '../stores/projectStore';
import {
  useProjectReports,
  useCreateProjectReport,
  useRegenerateProjectReport,
  useUpdateProjectReportShare,
  useDeleteProjectReport,
} from '../hooks/useProjectReports';

function firstDayOfMonth(offset = 0) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return d.toISOString().slice(0, 10);
}

function lastDayOfMonth(offset = 0) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset + 1);
  d.setDate(0);
  return d.toISOString().slice(0, 10);
}

export default function ProjectReports() {
  const project = useProjectStore((s) => s.projects.find((p) => p.id === s.currentProjectId));
  const { data: reports = [], isLoading } = useProjectReports();
  const create = useCreateProjectReport();
  const regenerate = useRegenerateProjectReport();
  const updateShare = useUpdateProjectReportShare();
  const deleteReport = useDeleteProjectReport();

  const [createOpen, setCreateOpen] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const [shareModal, setShareModal] = useState(null);
  const [sharePassword, setSharePassword] = useState('');
  const [copied, setCopied] = useState(false);

  const [form, setForm] = useState({
    title: '',
    period_start: firstDayOfMonth(-1),
    period_end: lastDayOfMonth(-1),
  });

  const previewReport = useMemo(
    () => reports.find((r) => r.id === previewId),
    [reports, previewId],
  );

  const handleCreate = async () => {
    if (!project) return;
    await create.mutateAsync({
      project_id: project.id,
      title: form.title || undefined,
      period_start: form.period_start,
      period_end: form.period_end,
    });
    setCreateOpen(false);
    setForm({ title: '', period_start: firstDayOfMonth(-1), period_end: lastDayOfMonth(-1) });
  };

  const shareUrl = (report) =>
    `${window.location.origin}/share/project-report/${report.share_token}`;

  if (!project) {
    return (
      <div>
        <ProjectContextBadge />
        <div className="bg-white rounded-xl border border-black/[.07] p-10 text-center text-gray-500 text-sm">
          Wähle zuerst ein Projekt im Project-Switcher.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-5">
        <div>
          <ProjectContextBadge />
          <h1 className="text-[23px] font-bold mb-0.5">Monatsreports</h1>
          <p className="text-gray-500 text-[13.5px]">
            Snapshot-Reports über CRO-Analysen, SEO-Rankings und Maßnahmen pro Zeitraum
          </p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="px-4 py-2 bg-teal text-white rounded-lg text-[13px] font-medium hover:bg-teal/90"
        >
          + Neuer Report
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-black/[.07]">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 text-left">
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Titel</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Zeitraum</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">CRO</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">SEO</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Sharing</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium text-right">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((r) => {
              const cro = r.data?.cro || {};
              const seo = r.data?.seo || {};
              return (
                <tr key={r.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/50">
                  <td className="px-5 py-3.5">
                    <div className="text-[13px] font-medium">{r.title}</div>
                    <div className="text-[11px] text-gray-500">
                      erstellt {new Date(r.created_at).toLocaleDateString('de')}
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-[12.5px] text-gray-600">
                    {r.data?.period?.label ||
                      `${new Date(r.period_start).toLocaleDateString('de')} – ${new Date(
                        r.period_end,
                      ).toLocaleDateString('de')}`}
                  </td>
                  <td className="px-5 py-3.5 text-[12.5px]">
                    {cro.analyses_count ?? 0} Analysen
                    {cro.avg_score != null && (
                      <span className="text-gray-500"> · Ø {cro.avg_score}</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-[12.5px]">
                    {seo.keywords_count ?? 0} Keywords
                    {seo.avg_position != null && (
                      <span className="text-gray-500"> · Ø Pos. {seo.avg_position}</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    {r.is_shared ? (
                      <button onClick={() => setShareModal(r)}>
                        <Badge bg="#D1FAE5" color="#10B981">Geteilt</Badge>
                      </button>
                    ) : (
                      <Badge bg="#F3F4F6" color="#6B7280">Privat</Badge>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => setPreviewId(r.id)}
                        className="text-[12px] text-teal font-medium hover:underline"
                      >
                        Ansehen
                      </button>
                      <button
                        onClick={() => window.open(`/api/project-reports/${r.id}/pdf`, '_blank')}
                        className="text-[12px] text-blue-600 font-medium hover:underline"
                      >
                        PDF
                      </button>
                      <button
                        onClick={() => regenerate.mutate(r.id)}
                        disabled={regenerate.isPending}
                        className="text-[12px] text-amber-600 font-medium hover:underline disabled:opacity-50"
                      >
                        Neu
                      </button>
                      <button
                        onClick={async () => {
                          await updateShare.mutateAsync({ id: r.id, is_shared: !r.is_shared });
                          if (!r.is_shared) setShareModal({ ...r, is_shared: true });
                        }}
                        className="text-[12px] text-purple-600 font-medium hover:underline"
                      >
                        {r.is_shared ? 'Link' : 'Teilen'}
                      </button>
                      <button
                        onClick={() => {
                          if (confirm('Report löschen?')) deleteReport.mutate(r.id);
                        }}
                        className="text-[12px] text-red-500 font-medium hover:underline"
                      >
                        Löschen
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {reports.length === 0 && !isLoading && (
          <div className="py-12 text-center text-gray-400 text-sm">
            Noch keine Monatsreports für dieses Projekt.
          </div>
        )}
        {isLoading && <div className="py-12 text-center text-gray-400 text-sm">Wird geladen…</div>}
      </div>

      {/* Create Modal */}
      {createOpen && (
        <Modal onClose={() => setCreateOpen(false)}>
          <div className="w-[460px]">
            <h2 className="text-[17px] font-bold mb-1">Neuer Monatsreport</h2>
            <p className="text-[13px] text-gray-500 mb-4">
              Snapshot für {project.name} — Daten werden direkt aggregiert und eingefroren.
            </p>

            <label className="text-[12px] text-gray-500 block mb-1 font-medium">
              Titel (optional)
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder={`${project.name} · Monatsreport`}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal mb-3"
            />

            <div className="grid grid-cols-2 gap-3 mb-5">
              <div>
                <label className="text-[12px] text-gray-500 block mb-1 font-medium">Von</label>
                <input
                  type="date"
                  value={form.period_start}
                  onChange={(e) => setForm({ ...form, period_start: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal"
                />
              </div>
              <div>
                <label className="text-[12px] text-gray-500 block mb-1 font-medium">Bis</label>
                <input
                  type="date"
                  value={form.period_end}
                  onChange={(e) => setForm({ ...form, period_end: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal"
                />
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setCreateOpen(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-[13px] hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                onClick={handleCreate}
                disabled={create.isPending}
                className="px-4 py-2 bg-teal text-white rounded-lg text-[13px] font-medium disabled:opacity-50"
              >
                {create.isPending ? 'Wird erstellt…' : 'Erstellen'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Preview Modal */}
      {previewId && previewReport && (
        <Modal onClose={() => setPreviewId(null)}>
          <div className="w-[min(1100px,95vw)] h-[85vh] flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[17px] font-bold">{previewReport.title}</div>
                <div className="text-[12px] text-gray-500">
                  {previewReport.data?.period?.label}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => window.open(`/api/project-reports/${previewId}/pdf`, '_blank')}
                  className="px-3 py-1.5 bg-teal text-white rounded-lg text-[12px] font-medium"
                >
                  PDF öffnen
                </button>
                <button
                  onClick={() => setPreviewId(null)}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg text-[12px]"
                >
                  Schließen
                </button>
              </div>
            </div>
            <iframe
              title="report-preview"
              src={`/api/project-reports/${previewId}/html`}
              className="flex-1 w-full border border-gray-200 rounded-lg bg-white"
            />
          </div>
        </Modal>
      )}

      {/* Share Modal */}
      {shareModal && (
        <Modal onClose={() => setShareModal(null)}>
          <div className="w-[460px]">
            <h2 className="text-[17px] font-bold mb-1">Share-Link</h2>
            <p className="text-[13px] text-gray-500 mb-4">
              Monatsreport teilen — öffentlicher Link, optional mit Passwort.
            </p>

            <div className="bg-bg rounded-lg p-3 mb-4">
              <div className="text-[11px] text-gray-500 mb-1">Share-URL</div>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={shareUrl(shareModal)}
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-[12px] font-mono bg-white outline-none"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(shareUrl(shareModal));
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  className="px-3 py-2 bg-teal text-white rounded-lg text-[12px] font-medium"
                >
                  {copied ? 'Kopiert!' : 'Kopieren'}
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">
                Passwort setzen (optional)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Mindestens 4 Zeichen"
                  value={sharePassword}
                  onChange={(e) => setSharePassword(e.target.value)}
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal"
                />
                <button
                  onClick={async () => {
                    if (sharePassword.length < 4) return;
                    await updateShare.mutateAsync({
                      id: shareModal.id,
                      is_shared: true,
                      share_password: sharePassword,
                    });
                    setSharePassword('');
                    setShareModal(null);
                  }}
                  disabled={sharePassword.length < 4}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-[12px] hover:bg-gray-50 disabled:opacity-50"
                >
                  Setzen
                </button>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                onClick={async () => {
                  await updateShare.mutateAsync({ id: shareModal.id, is_shared: false });
                  setShareModal(null);
                }}
                className="px-3 py-2 text-red-600 text-[12px] hover:underline"
              >
                Sharing deaktivieren
              </button>
              <button
                onClick={() => setShareModal(null)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-[13px] hover:bg-gray-50"
              >
                Schließen
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
